import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient.ts';
import { useTutorSession } from '../context/TutorSessionContext.tsx';

/**
 * Whether the current student belongs to a company. Public self-signup students
 * don't, so anything that needs company staff on the other end — lessons,
 * tutor-reviewed mark disputes — has to be kept away from them.
 *
 * null while the lookup is in flight; false when signed out.
 */
export function useHasCompany(): boolean | null {
  const { isTutorSession } = useTutorSession();
  const [hasCompany, setHasCompany] = useState<boolean | null>(null);

  useEffect(() => {
    // Tutor sessions only ever impersonate company students.
    if (isTutorSession) {
      setHasCompany(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user) {
        setHasCompany(false);
        return;
      }
      const { data } = await supabase
        .from('students')
        .select('company_id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (!cancelled) setHasCompany(!!data?.company_id);
    })();
    return () => {
      cancelled = true;
    };
  }, [isTutorSession]);

  return hasCompany;
}
